import { useEffect, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { formatApiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, AlertTriangle, Loader2, CalendarDays } from "lucide-react";

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";
  const [state, setState] = useState({ status: "verifying" });

  useEffect(() => {
    if (!token) {
      setState({ status: "error", message: "Missing verification token in the link." });
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token });
        setState({ status: "success", email: data.email });
        // Take the user straight to the login page after a brief beat so they
        // can act on the verification without hunting for a link.
        setTimeout(() => navigate("/login", { replace: true }), 2500);
      } catch (e) {
        setState({
          status: "error",
          message: formatApiError(e?.response?.data?.detail) || "We couldn't verify that link.",
        });
      }
    })();
  }, [token, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-background">
      <Card className="w-full max-w-md rounded-2xl">
        <CardContent className="pt-8 pb-8 text-center space-y-4">
          <div className="flex items-center justify-center gap-2 text-primary">
            <CalendarDays strokeWidth={1.5} className="size-6" />
            <span className="font-display font-extrabold text-xl tracking-tight">Lumivera</span>
          </div>

          {state.status === "verifying" && (
            <>
              <Loader2 strokeWidth={1.5} className="size-10 text-primary mx-auto animate-spin" data-testid="verify-loading" />
              <p className="text-sm text-muted-foreground">Verifying your email…</p>
            </>
          )}

          {state.status === "success" && (
            <div data-testid="verify-success" className="space-y-4">
              <div className="size-14 rounded-2xl bg-primary/15 flex items-center justify-center mx-auto">
                <CheckCircle2 strokeWidth={1.5} className="size-7 text-primary" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Email verified</h1>
              <p className="text-sm text-muted-foreground">
                You&apos;re all set. Sending you to sign in…
              </p>
              <Button asChild className="rounded-xl w-full" data-testid="verify-continue">
                <Link to="/login">Continue to sign in</Link>
              </Button>
            </div>
          )}

          {state.status === "error" && (
            <div data-testid="verify-error" className="space-y-4">
              <div className="size-14 rounded-2xl bg-destructive/15 flex items-center justify-center mx-auto">
                <AlertTriangle strokeWidth={1.5} className="size-7 text-destructive" />
              </div>
              <h1 className="font-display text-2xl font-bold tracking-tight">Verification failed</h1>
              <p className="text-sm text-muted-foreground">{state.message}</p>
              <Button asChild variant="outline" className="rounded-xl w-full">
                <Link to="/login">Back to sign in</Link>
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
